import _ from 'underscore';
import React from 'react';
import getConfig from 'next/config';
import Joi from 'joi';
import {
	api as apiRequest,
	batchApi as batchApiRequest
} from '@culture/common-helpers/request';
import { addCleanCache, parsePathParams } from '@culture/common-helpers/params';
import { addQuery, filterQuery } from '@culture/common-helpers/url';

import Template from 'pageTemplates/literature/poems';
import { getPushkinDayPromoBlock } from 'utils/helpers/pushkinDayPromoBlock';

const { serverRuntimeConfig } = getConfig();

const PoemsPage = (props) => <Template {...props} />;

const getRubricPage = (rubricPath, cleanCache) =>
	apiRequest(
		'rubricPages/page',
		addCleanCache(
			{
				rubricPath,
				completeBlocksParams: [
					{
						blockName: 'publications',
						blockParams: {
							limit: 6,
							rubricPaths: [serverRuntimeConfig.entities.literatureRubric],
							sort: '-publishDate'
						}
					}
				]
			},
			cleanCache
		)
	);

const getBreadcrumbs = (rubricPath, pathParams, cleanCache) =>
	apiRequest(
		'breadcrumbs',
		addCleanCache(
			{
				rubricPath,
				...pathParams,
				entityType: 'poems',
				cached: true
			},
			cleanCache
		)
	);

const getPoemsMeta = (urlParams, rubricPath, cleanCache) =>
	apiRequest(
		'meta/catalog',
		addCleanCache(
			{
				..._(urlParams).pick('author', 'tag', 'page'),
				rubricPath,
				cached: true
			},
			cleanCache
		)
	);

const getPoems = (urlParams, cleanCache) => {
	const poemsParams = filterQuery({
		..._(urlParams).pick('limit', 'page', 'query'),
		tagNames: urlParams.tag,
		authorName: urlParams.author,
		cached: true,
		statuses: ['published'],
		sort: '-views',
		fields: ['title', 'author', 'tags', 'name', 'text'].join(),
		limit: 24
	});

	return apiRequest('poems', addCleanCache(poemsParams, cleanCache));
};

const getPersons = (cleanCache) =>
	apiRequest(
		'persons',
		addCleanCache(
			{
				limit: 10,
				statuses: ['published'],
				sort: '-publishDate',
				rubrics: [serverRuntimeConfig.entities.poetRubric],
				fields: ['title', 'rubrics', 'country', 'name', 'thumbnailFile'].join(),
				cached: true
			},
			cleanCache
		)
	);

const getPerson = (author, cleanCache) =>
	apiRequest(
		'persons',
		addCleanCache(
			{
				names: [author],
				fields: [
					'title',
					'name',
					'thumbnailFile',
					'biography',
					'status'
				].join(),
				cached: true,
				isExtend: true
			},
			cleanCache
		)
	);

const getFilters = (urlParams, cleanCache) => {
	const filterParams = filterQuery({
		authorName: urlParams.author,
		query: urlParams.query,
		cached: true
	});

	return apiRequest(
		'catalogFilters/literature/poems',
		addCleanCache(filterParams, cleanCache)
	);
};

export async function getServerSideProps({ query, resolvedUrl }) {
	const validationSchema = Joi.object().keys({
		cleanCache: Joi.boolean().failover(false),
		limit: Joi.number().default(45).min(1),
		query: Joi.string(),
		page: Joi.number().default(1).min(1),
		pathParams: Joi.array().items(Joi.string())
	});

	const pathParamsValidationSchema = Joi.object().keys({
		author: Joi.string(),
		tag: Joi.string()
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const parsedPathParams = parsePathParams(params?.pathParams);

	const pathParams = Joi.attempt(
		parsedPathParams.params,
		pathParamsValidationSchema,
		{ stripUnknown: true }
	);

	const rubricPath = 'literature/poems';
	const urlParams = { ..._(params).omit('pathParams'), ...pathParams };

	const [
		rubricPage,
		breadcrumbs,
		poems,
		meta,
		{ items: persons },
		author,
		filters,
		pushkinDayPromoBlock
	] = await batchApiRequest([
		getRubricPage(rubricPath, params.cleanCache),
		getBreadcrumbs(rubricPath, pathParams, params.cleanCache),
		getPoems(urlParams, params.cleanCache),
		getPoemsMeta(urlParams, rubricPath, params.cleanCache),
		getPersons(params.cleanCache),
		urlParams.author
			? getPerson(urlParams.author, params.cleanCache)
			: undefined,
		getFilters(urlParams, params.cleanCache),
		getPushkinDayPromoBlock()
	]);

	let aboutAuthor = null;

	if (author && author.items.length) {
		aboutAuthor =
			author.items.find((item) => item.status === 'published') ||
			author.items[0];
	}

	if (poems.pagination.total && poems.pagination.total < params.page) {
		return {
			redirect: {
				destination: addQuery(`${serverRuntimeConfig.baseUrl}${resolvedUrl}`, {
					page: poems.pagination.total
				}),
				permanent: false
			}
		};
	}

	return {
		props: {
			pagination: poems.pagination,
			urlParams: _(urlParams).omit('limit'),
			rubric: rubricPage.rubric,
			breadcrumbs,
			publications: rubricPage.blocks.publications,
			persons,
			author: aboutAuthor,
			poems: poems.items,
			meta,
			filters,
			pushkinDayPromoBlock: _(pushkinDayPromoBlock).pick('shortText', 'text')
		}
	};
}

export default PoemsPage;
